import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { api } from '../api/client.js';

export interface FeatureState {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
}

/**
 * Feature-Ebene (v6/E4): welche Kern-Funktionen sind für den aktiven
 * Mandanten an? Navigation und Routen folgen dem Ergebnis; die API bleibt
 * die eigentliche Wache (404 bei abgeschalteter Funktion).
 *
 * Query-Key ['features'] ist bewusst derselbe wie in @aurora/web-widgets
 * (useFeatureEnabled) — gleiche QueryClient-Instanz, ein Request für alle.
 * Solange die Antwort aussteht, gilt AN (Server-Default ist an).
 */
export function useFeatures(): {
  features: FeatureState[];
  has: (key: string) => boolean;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ['features'],
    queryFn: () => api<{ features: FeatureState[] }>('/api/v1/features'),
    staleTime: 60_000,
  });
  const features = useMemo(() => query.data?.features ?? [], [query.data]);
  // Referenzstabil — `has` wird in der Shell als useMemo-Abhängigkeit benutzt.
  const has = useCallback(
    (key: string) => features.find((state) => state.key === key)?.enabled ?? true,
    [features],
  );
  return { features, has, isLoading: query.isLoading };
}
